import React, { useState, useEffect } from "react";
import { get } from "lodash";
import DOMPurify from "dompurify";
import { intervalToDuration, isValid, isBefore } from "date-fns";
import { format } from "date-fns-tz";
import buildColor from "@fdr/static-ui/ColorPalette";
import Text from "@tvg/atomic-ui/_atom/Text";
import { DerbyEventStatus, HomepageEventBannerText } from "@fdr/types/ts/Derby";
import {
  Container,
  Body,
  MiddleBorder,
  TimerNumber,
  TimerTimeUnit,
  EventDescription
} from "./styled-component";

const ONE_SECOND = 1000;
const twoDigits = (num: number) => String(num).padStart(2, "0");

interface Props {
  // End date in Date format
  endDate?: Date | null;
  // race status
  raceStatus: DerbyEventStatus;
  // qaLabel
  qaLabel?: string;
  forceFullDate?: boolean;
  raceNumber?: number;
  description?: HomepageEventBannerText;
}

interface HomepageBannerTexts {
  label: string;
  size: number;
}

const retrieveComponentByRaceStatus = (
  raceStatus: DerbyEventStatus,
  hoursToDisplay: number,
  minutesToDisplay: number,
  secondsToDisplay: number,
  raceStartDate: Date | null = null,
  forceFullDate = false,
  headerStatus?: DerbyEventStatus,
  raceNumber?: number,
  description?: HomepageBannerTexts
) => {
  const descriptionText = get(description, "label");
  const eventEnded = headerStatus === DerbyEventStatus.POST;

  if (typeof window !== "undefined" && !eventEnded && descriptionText) {
    return (
      <EventDescription
        data-qa-label="event-description"
        raceEnded={eventEnded}
        $fontSize={get(description, "size", 16)}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(descriptionText)
        }}
      />
    );
  }

  const hasValidRaceDate = raceStartDate && isValid(raceStartDate);

  if (
    !descriptionText &&
    forceFullDate &&
    hasValidRaceDate &&
    raceStatus === DerbyEventStatus.DERBY_DAY
  ) {
    return (
      <Body>
        <Text tag="div" fontSize={14} className="countdown-text">
          Starting AT {format(raceStartDate, "hh:mm a zzz")}
          {raceNumber && ` - Race ${raceNumber}`}
        </Text>
      </Body>
    );
  }

  const defaultRender = (
    <Body data-qa-label="no-event-title" raceEnded={eventEnded}>
      <Text
        tag="div"
        fontSize={16}
        color={buildColor("grey", "300")}
        className="no-event-title"
      >
        Bet and watch races
        <Text
          tag="div"
          fontSize={16}
          color={buildColor("yellow")}
          className="no-event-title"
        >
          LIVE
        </Text>
      </Text>
    </Body>
  );

  switch (raceStatus) {
    case DerbyEventStatus.DERBY_DAY:
      return hoursToDisplay || minutesToDisplay || secondsToDisplay ? (
        <Body data-qa-label="timer">
          <Text
            fontSize={16}
            tag="div"
            color={buildColor("grey", "300")}
            className="countdown-text"
          >
            Starts in
          </Text>
          <TimerNumber>{twoDigits(hoursToDisplay)}</TimerNumber>
          <TimerTimeUnit>H</TimerTimeUnit>
          <TimerNumber>{twoDigits(minutesToDisplay)}</TimerNumber>
          <TimerTimeUnit>M</TimerTimeUnit>
          <TimerNumber>{twoDigits(secondsToDisplay)}</TimerNumber>
          <TimerTimeUnit>S</TimerTimeUnit>
        </Body>
      ) : (
        defaultRender
      );
    case DerbyEventStatus.PRE:
      return raceStartDate && isValid(raceStartDate) ? (
        <Body>
          <Text tag="div" fontSize={14} className="countdown-text">
            {format(raceStartDate, "MMM dd, yyyy - hh:mm a zzz")}
          </Text>
        </Body>
      ) : (
        defaultRender
      );
    default:
      return defaultRender;
  }
};

export const Countdown = ({
  qaLabel = "countdown",
  raceStatus = DerbyEventStatus.POST,
  endDate,
  forceFullDate = false,
  raceNumber,
  description
}: Props) => {
  const [countdown, setCountdown] = useState(new Date());
  const [intervalId, setIntervalId] = useState<ReturnType<
    typeof setInterval
  > | null>(null);
  useEffect(() => {
    const isEventActive = raceStatus !== DerbyEventStatus.POST;

    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    if (isEventActive) {
      const id = setInterval(() => {
        setCountdown(new Date());
      }, ONE_SECOND);

      setIntervalId(id);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [raceStatus]);

  const { hours, minutes, seconds } =
    countdown && countdown && endDate && isValid(endDate)
      ? intervalToDuration({
          start: new Date(countdown),
          end: endDate
        })
      : {
          hours: 0,
          minutes: 0,
          seconds: 0
        };

  return (
    <Container data-qa-label={qaLabel}>
      {retrieveComponentByRaceStatus(
        !endDate || isBefore(countdown, endDate)
          ? raceStatus
          : DerbyEventStatus.POST,
        hours || 0,
        minutes || 0,
        seconds || 0,
        endDate,
        forceFullDate,
        raceStatus,
        raceNumber,
        description
      )}
      <MiddleBorder />
    </Container>
  );
};
